/**
 * 高德地图 JS API 统一加载器（全局唯一，修复阶段 17 排错发现的插件冲突）。
 *
 * 背景 bug：map.tsx 与 map-picker.tsx 各自维护 loadAMap，共享 window.__amap_loaded__
 * 但 plugin 列表不同 —— 先加载者若缺少 PlaceSearch，后加载的选点组件拿到的是
 * 没挂 PlaceSearch 的 AMap，报 "AMap.PlaceSearch is not a constructor"。
 *
 * 修复策略：
 * 1. plugin 列表统一为超集，全站只有这一处加载入口；
 * 2. ensureAMapPlugin(name) 动态补载兜底 —— 即使 AMap 已被旧版本脚本加载
 *   （或第三方页面）也能按需挂上缺的插件。
 *
 * Key 来源（阶段 23 改造）：不再读 .env.local（NEXT_PUBLIC_AMAP_KEY 会被内联进
 * 前端产物，且换 Key 要重新构建），改为运行时从后端拉取：
 *   GET {API}/api/dk/common/amap-config  →  { key, security }
 * 后台（Dashboard-DK 地图配置页）可随时更换 Key，前端刷新即生效。
 */

import { BASE_URL } from "@/lib/api/client";

const AMap_PLUGINS = [
  "AMap.Driving",
  "AMap.Geocoder",
  "AMap.Geolocation",
  "AMap.PlaceSearch",
  "AMap.ToolBar",
  "AMap.Scale",
  "AMap.AutoComplete",
];

declare global {
  interface Window {
    AMap?: any;
    __amap_loaded__?: Promise<any>;
  }
}

type AMapConfig = { key: string; security: string };

/** 已拉取到的后端配置（模块级缓存，跨组件共享） */
let cachedConfig: AMapConfig | null = null;
let pendingConfig: Promise<AMapConfig> | null = null;

/**
 * 从后端获取高德 Key（带缓存；失败返回空 key，由调用方按"未配置"处理）。
 */
export function fetchAMapConfig(): Promise<AMapConfig> {
  if (cachedConfig) return Promise.resolve(cachedConfig);
  pendingConfig ??= fetch(`${BASE_URL}/api/dk/common/amap-config`, {
    headers: { "Content-Type": "application/json" },
  })
    .then((r) => r.json())
    .then((d: any) => {
      const cfg: AMapConfig = {
        key: (d?.data?.key || "").trim(),
        security: (d?.data?.security || "").trim(),
      };
      cachedConfig = cfg;
      return cfg;
    })
    .catch(() => {
      // 网络失败：缓存空结果，1s 后允许重试
      cachedConfig = { key: "", security: "" };
      return cachedConfig;
    })
    .finally(() => {
      setTimeout(() => (pendingConfig = null), 1000);
    });
  return pendingConfig;
}

export function loadAMap(): Promise<any> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("AMap 只能在客户端加载"));
  }
  if (window.AMap) return Promise.resolve(window.AMap);
  if (window.__amap_loaded__) return window.__amap_loaded__;

  window.__amap_loaded__ = fetchAMapConfig().then(({ key, security }) => {
    if (!key) {
      throw new Error("未配置高德地图 Key（请在后台「地图配置」设置）");
    }
    if (security) {
      (window as any)._AMapSecurityConfig = { securityJsCode: security };
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://webapi.amap.com/maps?v=2.0&key=${key}&plugin=${AMap_PLUGINS.join(",")}`;
      script.async = true;
      script.referrerPolicy = "no-referrer";
      script.onload = () => resolve(window.AMap);
      script.onerror = () =>
        reject(new Error("AMap JS 文件加载失败（可能是网络/代理问题）"));
      document.head.appendChild(script);
      // 兜底：15s 内未加载成功也 reject，避免永久卡住
      setTimeout(() => {
        if (!window.AMap) reject(new Error("AMap JS 加载超时"));
      }, 15000);
    });
  });
  return window.__amap_loaded__;
}

/**
 * 确保某个插件已挂载（如 "AMap.PlaceSearch"），返回其构造函数。
 * 已挂载直接返回；否则用 AMap.plugin() 动态补载后返回。
 */
export function ensureAMapPlugin(name: string): Promise<any> {
  // 注意：AMap v2.0 把插件构造函数挂在「短名」属性上 ——
  // AMap.plugin("AMap.Geocoder") 之后是 window.AMap.Geocoder，
  // 不存在 window.AMap["AMap.Geocoder"] 这种全名键。
  const short = name.split(".").pop() as string;
  return loadAMap().then(
    (AMap) =>
      new Promise((resolve, reject) => {
        if (AMap[short]) return resolve(AMap[short]);
        AMap.plugin(name, () => {
          const ctor = (window.AMap as any)?.[short];
          if (ctor) resolve(ctor);
          else reject(new Error(`AMap 插件 ${name} 加载失败`));
        });
      }),
  );
}

/**
 * 是否确定没有可用 Key（同步）。
 * 配置是运行时异步拉的：拉取完成前返回 false（乐观放行，由 loadAMap 兜底报错），
 * 拉取完且为空才返回 true（调用方跳过依赖地图的逻辑）。
 */
export function amapKeyMissing(): boolean {
  return cachedConfig !== null && !cachedConfig.key;
}
